import { FileText, LoaderCircle, MessageSquareText, PencilRuler, Share2, TextCursorInput, Trash2, TriangleAlert, User } from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { DeleteProgramModal } from "@/components/modals/DeleteProgramModal"
import { ForkProgramOverlay } from "@/components/overlays/ForkProgramOverlay"
import { Badge } from "@ui/badge"
import { Button } from "@ui/button"
import { useUserCache } from "@hooks/useUserCache"
import { toLocaleDateTimeString } from "@/lib/date"
import { useUserSession } from "@session/UserSession"

import { CollectionItem } from "@vertesia/common"

interface ReportRunIconProps {
    report: CollectionItem
    onDelete: (programId: string) => void
    onClose: () => void
}
export function ReportRunIcon({ report, onDelete, onClose }: Readonly<ReportRunIconProps>) {
    const navigate = useNavigate()
    const { client } = useUserSession()
    const { getUserDisplayName } = useUserCache(client)

    const reportName = report.name || report.id

    const properties = (report as any)?.properties || {}
    const runId = properties.run_id || undefined
    const workflowId = properties.workflow_id || undefined
    const status = properties.status || "UNKNOWN"
    const inputDocuments = [...properties?.documents || [], ...properties?.additional_documents || []]
    const configurationId = properties?.configuration || undefined
    const [configuration, setConfiguration] = useState<string | undefined>(configurationId)
    const templateId = properties?.template
    const [template, setTemplate] = useState<string>(templateId || "Missing information")

    const copyToClipboard = () => {
        navigator.clipboard.writeText(`${window.location.href}/${report.id}`)
            .then(() => {
                toast.success("Report link copied to clipboard!", {
                    duration: 2000
                })
            })
    }

    useEffect(() => {
        if (configurationId) {
            client.store.collections.retrieve(configurationId)
                .then((configurationData) => {
                    setConfiguration(configurationData.name || undefined)
                })
                .catch(() => {
                    setConfiguration(undefined)
                })
        } else {
            setConfiguration(undefined)
        }
    }, [configurationId, client])

    useEffect(() => {
        if (templateId) {
            client.store.objects.retrieve(templateId)
                .then((templateData) => {
                    setTemplate(templateData.name || "Template Information Missing")
                })
                .catch(() => {
                    setTemplate("Template Information Missing")
                })
        } else {
            setTemplate("Template Information Missing")
        }
    }, [templateId, client])

    return (
        <div className="flex flex-col w-full bg-white rounded-sm border border-gray-300 p-4 gap-2 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-gray-900">{reportName}</span>
                    {
                        status === "processing" ? (
                            <Badge className="flex gap-2 text-sm bg-blue-200 text-blue-800 hover:cursor-pointer hover:bg-blue-300 hover:text-blue-900 capitalize">
                                <LoaderCircle className="size-4 animate-spin motion-safe:animate-[spin_2s_linear_infinite]" />
                                Processing
                            </Badge>
                        ) : status === "ready" ? (
                            <Badge className="flex gap-2 text-sm bg-green-200 text-green-800 hover:cursor-pointer hover:bg-green-300 hover:text-green-900 capitalize">
                                <FileText className="size-4" />
                                Ready
                            </Badge>
                        ) : status === "error" ? (
                            <Badge className="flex gap-2 text-sm bg-red-200 text-red-800 hover:cursor-pointer hover:bg-red-300 hover:text-red-900 capitalize">
                                <TriangleAlert className="size-4" />
                                Error
                            </Badge>
                        ) : status === "configuration" ? (
                            <Badge className="flex gap-2 text-sm bg-orange-200 text-orange-800 hover:cursor-pointer hover:bg-orange-300 hover:text-orange-900 capitalize">
                                <TextCursorInput className="size-4" />
                                Configuration Needed
                            </Badge>
                        ) : (
                            <Badge className="flex gap-2 text-sm bg-fuchsia-200 text-fuchsia-800 hover:cursor-pointer hover:bg-fuchsia-300 hover:text-fuchsia-900 capitalize">
                                <MessageSquareText className="size-4" />
                                Waiting for input
                            </Badge>
                        )
                    }
                </div>
                <div>
                    {
                        configuration &&
                        <Badge className="flex text-sm capitalize">
                            {configuration}
                        </Badge>
                    }
                </div>
            </div>
            <div>
                <div className='flex items-center gap-1'>
                    <User size={16} className="text-gray-800" />
                    <p className="text-base text-gray-800">{getUserDisplayName(report.created_by, "AI Report Generator")}</p>
                </div>
                <div className='flex items-center gap-2'>
                    <p className="text-sm text-gray-600">Created {toLocaleDateTimeString(new Date(report.created_at))}</p>
                    <span className='text-lg'>•</span>
                    <p className="text-sm text-gray-600">Updated {toLocaleDateTimeString(new Date(report.updated_at))}</p>
                    <span className='text-lg'>•</span>
                    <p className="text-sm text-gray-600">{inputDocuments.length} document{inputDocuments.length > 1 ? "s" : ""}</p>
                    <span className='text-lg'>•</span>
                    <p className="text-sm text-gray-600">{template}</p>
                </div>
            </div>
            <div className='flex items-center justify-between gap-2'>
                <div className='flex items-center gap-2'>
                    {
                        status === "configuration" && (
                            <ForkProgramOverlay initialCollection={report} onClose={onClose}>
                                <Button>
                                    <PencilRuler />
                                    Edit
                                </Button>
                            </ForkProgramOverlay>
                        )
                    }
                    {
                        status !== "configuration" && (
                            <Button variant='outline' onClick={() => copyToClipboard()}>
                                <Share2 />
                                Share
                            </Button>
                        )
                    }
                    {
                        status !== "configuration" && (
                            <Button onClick={() => navigate(`/report-generator/${report.id}`)}>
                                <FileText />
                                View
                            </Button>
                        )
                    }
                </div>
                {
                    ((status && status !== "processing" && status !== "UNKNOWN") || !runId || !workflowId) && (
                        <div>
                            <DeleteProgramModal program={report.id} onDelete={() => onDelete(report.id)}>
                                <Button variant="destructive">
                                    <Trash2 />
                                    Delete
                                </Button>
                            </DeleteProgramModal>
                        </div>
                    )
                }
            </div>
        </div>
    )
}